import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as authApi from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'

export function MfaSetup() {
  const { user, loading, mfaEnabled, requireMfa, setUser, logout } = useAuth()
  const navigate = useNavigate()
  const [setup, setSetup] = useState(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loadingSetup, setLoadingSetup] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!user || mfaEnabled || !requireMfa) return
      setLoadingSetup(true)
      setError('')
      try {
        const data = await authApi.setupMfa()
        if (!cancelled) setSetup(data)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not start 2FA setup.',
          )
        }
      } finally {
        if (!cancelled) setLoadingSetup(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [user, mfaEnabled, requireMfa])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#eef2f6] to-[#f8fafc]">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!requireMfa || mfaEnabled) {
    return <Navigate to="/" replace />
  }

  async function handleConfirm(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const updated = await authApi.confirmMfa(code.trim())
      setUser(updated)
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Invalid code. Try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#eef2f6] to-[#f8fafc] p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-8 shadow-sm">
        <p className="text-2xl font-semibold tracking-tight text-foreground">Set up 2FA</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Scan the QR code with Google Authenticator (or similar), then enter the
          6-digit code to finish. Two-factor authentication is required for all
          accounts.
        </p>

        {loadingSetup && <p className="mt-6 text-sm text-muted-foreground">Preparing…</p>}

        {setup && (
          <div className="mt-6 space-y-4">
            <img
              src={setup.qr_code_data_url}
              alt="Authenticator QR code"
              className="mx-auto h-48 w-48 rounded-md border border-border bg-white p-2"
            />
            <p className="text-center text-xs text-muted-foreground">
              Or enter this key manually:{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                {setup.secret}
              </code>
            </p>

            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-foreground">
                  Authenticator code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={submitting}
                  className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 tracking-widest text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? 'Confirming…' : 'Confirm and continue'}
              </button>
            </form>
          </div>
        )}

        {error && !setup && (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={() => logout()}
          className="mt-6 text-sm text-muted-foreground underline"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
